import { Component, Inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';

export interface InstructorLessonsFilterDialogData {
  dateFrom: string;
  dateTo: string;
  studentId: string | number | null;
  students: Array<{ id: string | number; label: string }>;
}

export interface InstructorLessonsFilterDialogResult {
  dateFrom: string;
  dateTo: string;
  studentId: string | number | null;
}

@Component({
  selector: 'app-instructor-lessons-filter-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    TranslatePipe
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  template: `
    <h2 mat-dialog-title>{{ 'instructor.filterLessons.title' | t }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="dialog-grid">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'common.from' | t }}</mat-label>
          <input matInput [matDatepicker]="dateFromPicker" formControlName="dateFrom" />
          <mat-datepicker-toggle matIconSuffix [for]="dateFromPicker" />
          <mat-datepicker #dateFromPicker />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'common.to' | t }}</mat-label>
          <input matInput [matDatepicker]="dateToPicker" formControlName="dateTo" />
          <mat-datepicker-toggle matIconSuffix [for]="dateToPicker" />
          <mat-datepicker #dateToPicker />
        </mat-form-field>
        <mat-form-field appearance="outline" class="span-2">
          <mat-label>{{ 'common.student' | t }}</mat-label>
          <mat-select formControlName="studentId">
            <mat-option [value]="null">{{ 'common.all' | t }}</mat-option>
            @for (item of data.students; track item.id) {
              <mat-option [value]="item.id">{{ item.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-button (click)="clear()">{{ 'common.clear' | t }}</button>
      <button mat-flat-button color="primary" (click)="apply()">{{ 'common.apply' | t }}</button>
    </mat-dialog-actions>
  `,
  styles: `
    .dialog-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      min-width: 460px;
      margin-top: 8px;
    }
    .span-2 {
      grid-column: span 2;
    }
    @media (max-width: 760px) {
      .dialog-grid {
        grid-template-columns: 1fr;
        min-width: 280px;
      }
      .span-2 {
        grid-column: auto;
      }
    }
  `
})
export class InstructorLessonsFilterDialogComponent {
  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<InstructorLessonsFilterDialogComponent, InstructorLessonsFilterDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: InstructorLessonsFilterDialogData
  ) {
    this.form = this.fb.nonNullable.group({
      dateFrom: this.toDateValue(this.data.dateFrom),
      dateTo: this.toDateValue(this.data.dateTo),
      studentId: this.data.studentId as string | number | null
    });
  }

  clear(): void {
    this.dialogRef.close({
      dateFrom: '',
      dateTo: '',
      studentId: null
    });
  }

  apply(): void {
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      dateFrom: this.toIsoDate(raw.dateFrom),
      dateTo: this.toIsoDate(raw.dateTo),
      studentId: raw.studentId ?? null
    });
  }

  private toDateValue(value: unknown): Date | string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return value;
    }
    const text = `${value}`.trim();
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (isoMatch) {
      const [, yearRaw, monthRaw, dayRaw] = isoMatch;
      return new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
    }
    return text;
  }

  private toIsoDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date && Number.isFinite(value.getTime())) {
      return [
        value.getFullYear(),
        String(value.getMonth() + 1).padStart(2, '0'),
        String(value.getDate()).padStart(2, '0')
      ].join('-');
    }
    return `${value}`.trim();
  }
}

